import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Mail, Check, BookOpen, Sparkles, Plane, Globe, Compass, Send, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { isDisposableEmail, DISPOSABLE_EMAIL_ERROR } from '@/utils/disposableEmailDomains';

const emailSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .refine((val) => !isDisposableEmail(val), { message: DISPOSABLE_EMAIL_ERROR }),
});
type EmailFormData = z.infer<typeof emailSchema>;

interface NewsletterSignupProps {
  showBlogLink?: boolean;
}

export const NewsletterSignup = ({ showBlogLink = true }: NewsletterSignupProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
  });

  const onSubmit = async (data: EmailFormData) => {
    try {
      const { error } = await supabase.from('newsletter_subscribers').insert([
        { email: data.email.trim().toLowerCase() },
      ]);
      if (error) {
        if (error.code === '23505') {
          toast({
            title: 'Already subscribed!',
            description: 'This email is already on our newsletter list.',
          });
        } else {
          throw error;
        }
      } else {
        setIsSubmitted(true);
        toast({
          title: 'Welcome aboard!',
          description: "You've successfully subscribed to our newsletter.",
        });
        setTimeout(() => setIsSubmitted(false), 3000);
      }
      reset();
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const chips = [
    { icon: Plane, label: 'Travel Tips' },
    { icon: Globe, label: 'Destination Guides' },
    { icon: Compass, label: 'Insider Hacks' },
  ];

  return (
    <section className="py-16 sm:py-20 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-card via-background to-card border border-primary/10 shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.15)] px-6 sm:px-12 py-14 sm:py-20">
          {/* Soft background glow */}
          <div className="absolute inset-0 pointer-events-none opacity-60">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          </div>

          <div className="relative text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-background/80 backdrop-blur px-4 py-2 rounded-full border border-primary/20 mb-8">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Stay Connected</span>
            </div>

            {/* Headline */}
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6 tracking-tight">
              Never Miss a <span className="text-primary">Travel Tip</span>
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
              Get expert travel tips, packing guides, and exclusive travel inspiration delivered straight to your inbox.
            </p>

            {/* Chips */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
              {chips.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-2 bg-background/80 backdrop-blur px-4 py-2 rounded-full border border-primary/20 text-sm font-medium text-foreground"
                >
                  <Icon className="w-4 h-4 text-primary" />
                  {label}
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-2 bg-background rounded-2xl p-2 shadow-lg border border-border">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                  <Input
                    {...register('email')}
                    type="email"
                    placeholder="your@email.com"
                    disabled={isSubmitting}
                    className="h-12 sm:h-14 pl-12 pr-4 text-base border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting || isSubmitted}
                  className="h-12 sm:h-14 px-8 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl gap-2 text-base font-semibold"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Signing Up...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <Check className="w-4 h-4" /> Subscribed!
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" /> Subscribe
                    </>
                  )}
                </Button>
              </div>
              {errors.email && (
                <p className="text-destructive text-sm mt-2 text-left pl-2">{errors.email.message}</p>
              )}
            </form>

            {/* Privacy */}
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              We respect your privacy. Unsubscribe anytime.
            </div>

            {/* Blog link */}
            {showBlogLink && (
              <div className="mt-8">
                <button
                  onClick={() => {
                    navigate('/blog');
                    window.scrollTo(0, 0);
                  }}
                  className="inline-flex items-center gap-2 text-foreground font-medium hover:text-primary transition-colors"
                >
                  <BookOpen className="h-4 w-4" />
                  Read Our Travel Blog
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
